<template>
    <div class="container">
        <div id="editor-container" ref="editor" />
    </div>
</template>

<script>
import { createBaseOptions, KeapEvents } from './utils';
import clonedeep from 'lodash.clonedeep';
import { toRaw } from 'vue';
import packageFile from '../../package.json'

const PERMITTED_FEATURE_KEYS = [
    'ab-testing',
    'add-form-id',
    'ai',
    'audit',
    'page-anchors',
    'autonameSection',
    'preheader-text',
    'emojis',
    'kmn-opt-in',
    'fullstory',
    'form-tool',
    'form-tag-create',
    'pages-panel',
    'settings-panel',
    'smart-merge-tags',
    'ab-testing',
    'appointment-tool',
    'checkout-tool',
    'publish-settings',
    'analytics-settings',
    'upload-panel',
    'favicon-settings',
    'create-virtual-field',
    'url-params',
    'brand-logo-tool',
    'calendly',
    'progress-tool',
    'popup-panel'
]
export default {
    name: 'UnlayerEditor',
    props: {
        design: Object,
        displayMode: String,
        editorVersion: String,
        keapUnlayerTools: String,
        maxPageCount: Number,
        projectId: {
            type: String,
            required: true,
        },
        options: {
            type: Object,
            required: true,
            default: () => ({ features: [] }),
        },
    },
    data() {
        return {
            editor: null,
            pageData: {},
        };
    },
    mounted() {
        this.initializeEditor();
        this.loadScript();
    },

    watch: {
        design: {
            deep: true,
            handler(newDesign) {
                if (this.editorRaw) {
                    this.editorRaw.loadDesign(clonedeep(newDesign));
                }
            },
        },
    },
    computed: {
        editorRaw() {
            return toRaw(this.editor);
        },
        editorOptions() {
            const baseOptions = createBaseOptions(this.options || {});

            const projectId = this.projectId;
            const version = this.editorVersion;

            const editorOptions = {
                ...baseOptions,
                id: 'editor-container',
                projectId,
                displayMode: this.displayMode,
                features: this.unlayerFeatures,
                customJS: this.customTools,
            };

            if (this.options.appId) {
                editorOptions.user = { id: this.options.appId }
            }

            if (version) {
                editorOptions.version = version;
            }

            return editorOptions;
        },

        unlayerFeatures() {
            return {
                ai: this.options.features.includes('ai')
                    ? {
                      smartText: true,
                      smartButtons: true,
                      magicImage: false,
                      smartImageAltText: false,
                      smartHeadings: true,
                    }
                    : false,
                smartMergeTags: this.options.features.includes('smart-merge-tags'),
                audit: this.options.features.includes('audit'),
                pageAnchors: this.options.features.includes('page-anchors'),
                preheaderText: this.options.features.includes('preheader-text'),
                userUploads: this.options.features.includes('upload-panel'),
                textEditor: {
                    emojis: this.options.features.includes('emojis'),
                },
            };
        },

        customTools() {
            const tools = [];
            const {
                site,
                localeData,
                locale,
                fullStoryIdentify,
                fullStoryBuilderEvent,
                fullStoryConfig,
                kmnAccount,
                domainSettingsUrl,
                tags,
                features,
                marketingPages = site.pages,
            } = (this.options || {});

            const { version } = packageFile;
            const keapUnlayerTools = this.keapUnlayerTools || `https://storage.googleapis.com/is-unlayer-tools.appspot.com/builds/keap-unlayer-tools-${version.substring(1)}.js`;

            tools.push(keapUnlayerTools);

            if (localeData) tools.push(`keapUnlayerTools.addLocaleData(${JSON.stringify(localeData)})`);
            if (locale) tools.push(`keapUnlayerTools.setCurrentLocale(${JSON.stringify(locale)})`);

            features.forEach((featureKey) => {
                switch (featureKey) {
                    case 'kmn-opt-in':
                        if (!kmnAccount || !kmnAccount.phoneNumber) {
                            throw new Error('kmn-opt-in requires kmnAccount');
                        }
                        tools.push(`keapUnlayerTools.setKmnAccount(${kmnAccount.phoneNumber})`);
                        break;
                    case 'fullstory':
                        if (!fullStoryIdentify || !fullStoryBuilderEvent || !fullStoryConfig) {
                            throw new Error('fullstory requires fullStoryIdentify and fullStoryBuilderEvent');
                        }
                        tools.push(`keapUnlayerTools.fullStoryInit(${JSON.stringify(fullStoryConfig)}, true)`);
                        tools.push(`keapUnlayerTools.fullStoryIdentify('${fullStoryIdentify.casId}', ${JSON.stringify(fullStoryIdentify.identify)}, true)`);
                        tools.push(`keapUnlayerTools.fullStoryEvent("${fullStoryBuilderEvent.message}", ${JSON.stringify(fullStoryBuilderEvent.source)}, true)`);
                        break;
                    case 'form-tool':
                        tools.push('unlayer.registerPropertyEditor(keapUnlayerTools.KeapFormNamePropEditor)');
                        tools.push(`keapUnlayerTools.setAddFormId(${features.includes('add-form-id')})`);
                        tools.push(`unlayer.registerTool(keapUnlayerTools.KeapFormTool(${JSON.stringify(marketingPages)}, ${JSON.stringify(tags)}, ${features.includes('form-tag-create')}, ${features.includes('url-params')}))`);
                        tools.push('unlayer.registerPropertyEditor(keapUnlayerTools.KeapFieldsPropEditor)');
                        tools.push('unlayer.registerPropertyEditor(keapUnlayerTools.KeapSelectPropEditor)');
                        tools.push('unlayer.registerPropertyEditor(keapUnlayerTools.MultiSelectTagsEditor)');
                        tools.push('keapUnlayerTools.registerMarketingSiteLinkType()');
                        tools.push('keapUnlayerTools.registerMarketingPageLinkType()');
                        break;
                    case 'pages-panel':
                        if (!site) {
                            throw new Error('pages-panel requires \'site\' prop');
                        }
                        tools.push(`unlayer.registerTab(keapUnlayerTools.createPagesPanelSettings(${JSON.stringify(site.pages)}, ${this.maxPageCount}))`);
                        break;

                    case 'popup-panel':
                        tools.push(`unlayer.registerTab(keapUnlayerTools.popupPanelConfigurations())`);
                        break;
                    case 'settings-panel':
                        tools.push(`unlayer.registerTab(keapUnlayerTools.createSettingsPanel(${JSON.stringify(site)}, ${JSON.stringify({
                            showPublishSettings: features.includes('publish-settings'),
                            showAnalyticsSettings: features.includes('analytics-settings'),
                            showFaviconSettings: features.includes('favicon-settings'),
                            domainSettingsUrl,
                        })}))`);
                        break;
                    case 'ab-testing':
                        tools.push(`unlayer.registerTab(keapUnlayerTools.createABTestingPanel(${JSON.stringify({ audience: site.audience })}))`);
                        break;
                    case 'appointment-tool':
                        tools.push('keapUnlayerTools.registerAppointmentTypeLinkType()');
                        tools.push(`unlayer.registerTool(keapUnlayerTools.KeapAppointmentTool(${PERMITTED_FEATURE_KEYS.includes('calendly')}, ${JSON.stringify(tags)}))`);
                        tools.push('unlayer.registerPropertyEditor(keapUnlayerTools.CalendlyPropEditor)');
                        tools.push('unlayer.registerPropertyEditor(keapUnlayerTools.CalendlyTrackingEditor)');
                        break;
                    case 'checkout-tool':
                        tools.push('unlayer.registerPropertyEditor(keapUnlayerTools.KeapFieldsPropEditor)');
                        tools.push('unlayer.registerPropertyEditor(keapUnlayerTools.KeapSelectPropEditor)');
                        tools.push(`unlayer.registerTool(keapUnlayerTools.KeapCheckoutFormTool(${JSON.stringify(site.pages[0])}))`);
                        tools.push('keapUnlayerTools.registerCheckoutFormLinkType()');
                        break;
                    case 'brand-logo-tool':
                        tools.push('unlayer.registerTool(keapUnlayerTools.KeapLogoTool())')
                        tools.push('unlayer.registerPropertyEditor(keapUnlayerTools.KeapLogoEditor)')
                        break;
                    case 'progress-tool':
                        tools.push('unlayer.registerTool(keapUnlayerTools.KeapProgressTool())')
                        tools.push('unlayer.registerPropertyEditor(keapUnlayerTools.KeapProgressEditor)')
                        break;

                    default:
                        if (!PERMITTED_FEATURE_KEYS.includes(featureKey)) throw new Error(`No feature found named ${featureKey}!`);
                }
            });

            return tools;
        },
    },

    methods: {
        async loadScript() {
            const SCRIPT_URL = '//editor.unlayer.com/embed.js';
            const SCRIPT_ID = 'unlayer-js-api';

            return new Promise((resolve, reject) => {
                if (document.getElementById(SCRIPT_ID) == null) {
                    const script = document.createElement('script');

                    script.setAttribute('src', SCRIPT_URL);
                    script.setAttribute('id', SCRIPT_ID);

                    script.onload = resolve;
                    script.onerror = reject;

                    document.head.appendChild(script);
                } else {
                    resolve();
                }
            });
        },

        async initializeEditor() {
            await this.loadScript();
            this.editor = window.unlayer.createEditor(clonedeep(this.editorOptions));

            if (this.design) {
                this.editorRaw.loadDesign(clonedeep(this.design));
            }

            if (this.options.features.includes('upload-panel')) this.initializeImagesProvider();

            window.addEventListener('message', this.handleUnlayerMessage);
            this.editorRaw.addEventListener('design:loaded', async () => {
                await this.savePageData();
                this.$emit('register-provider', {
                    editor: this.editor,
                    getPageData: () => ({ ...this.pageData }),
                });
            });

            this.editorRaw.addEventListener('design:updated', (data) => {
                this.updatePageDesign({ data });
            });


        },

        initializeImagesProvider() {
            if (!this.editor) return
            this.editorRaw.registerProvider('userUploads', this.fetchImages);
            this.editorRaw.registerCallback('image', this.handleImageUpload);
            this.editorRaw.registerCallback('image:removed', this.handleImageDeleted)
        },

        async updatePageDesign({ data }) {
            if (!this.editor) return;
            const pageData = await this.savePageData();

            if (this.options.features.includes('autonameSection') && data?.type === 'row:added' && !data?.item?.values?.anchor) {
                this.autonameSection(pageData.design, data);
            }

            this.$emit('design-update', { data });
        },

        autonameSection(design, change) {
            const highestSectionNumber = design.body.rows.reduce((highestNumber, row) => {
                const sectionNumber = row.values.anchor && Number.parseInt(row.values.anchor.match(/^section_[0-9]+$/).input.split('_')[1], 10);

                return (!sectionNumber || sectionNumber < highestNumber) ? highestNumber : sectionNumber;
            }, 0);

            const rowIndex = design.body.rows.findIndex((row) => {
                if (row.id === change.item.id) {
                    return true;
                }

                if (row.columns.map((column) => column.id).includes(change.item.id)) {
                    return true;
                }

                return false;
            });

            design.body.rows[rowIndex].values = {
                ...design.body.rows[rowIndex].values,
                anchor: `section_${highestSectionNumber + 1}`,
            };

            this.editorRaw.loadDesign(design);

            return design;
        },

        handleUnlayerMessage({ data, ports }) {
            const { keapRequestType } = data;

            if (!keapRequestType) return;

            const postResponse = (response) => ports[0] && ports[0].postMessage({ success: true, response: clonedeep(response) });

            switch (keapRequestType) {
                case KeapEvents.showCreateVirtualField:
                    postResponse(this.options.features.includes('create-virtual-field'));
                    break;
                case KeapEvents.getCheckoutFormOptions:
                    postResponse(this.options.checkoutForms || []);
                    break;

                default:
                    this.$emit('unlayer-message', { data, postResponse });
                    break;
            }
        },

        async savePageData() {
            return new Promise((resolve) => {
              this.editorRaw.exportHtml((data) => {
                const pageData = data;
                this.pageData = pageData;
                resolve(pageData);
              });
            });
        },

        async fetchImages(params, done) {
            this.$emit('fetch-images', { params, done });
        },
        async handleImageUpload(params, done) {
            this.$emit('handle-image-upload', { params, done });
        },
        async handleImageDeleted(params, done) {
            this.$emit('handle-image-deleted', { params, done });
        },
    },
};
</script>

<style lang="scss" scoped>
.container,
#editor-container {
    height: 100%;
}
</style>
